import { useCallback, useEffect, useRef, useState } from 'react';

import * as styled from '../styles/video-recorder';

type Props = {
  onRecordingComplete: (file: File) => void;
  onCancel: () => void;
};

const MAX_VIDEO_DURATION_MS = 45_000;

const getMimeType = (): string => {
  if (typeof MediaRecorder === 'undefined') return '';
  if (MediaRecorder.isTypeSupported('video/mp4;codecs=avc1.42E01E,mp4a.40.2'))
    return 'video/mp4;codecs=avc1.42E01E,mp4a.40.2';
  if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
  if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus'))
    return 'video/webm;codecs=vp8,opus';
  if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
  return '';
};

export const VideoRecorder = ({ onRecordingComplete, onCancel }: Props) => {
  const [state, setState] = useState<Review.VideoRecorderState>('idle');
  const [elapsed, setElapsed] = useState(0);
  const [recordedUrl, setRecordedUrl] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<'user' | 'environment'>('user');

  const videoRef = useRef<HTMLVideoElement>(null);
  const playbackRef = useRef<HTMLVideoElement>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const blobRef = useRef<Blob | null>(null);

  const stopAll = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  useEffect(() => stopAll, [stopAll]);

  const startCamera = useCallback(
    (mode: 'user' | 'environment') => {
      stopAll();
      navigator.mediaDevices
        .getUserMedia({
          video: { facingMode: mode, width: { ideal: 720 } },
          audio: true,
        })
        .then((stream) => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
          }
          setState('idle');
        })
        .catch(() => {
          setError('Camera/mic access denied. Please allow permissions.');
        });
    },
    [stopAll],
  );

  useEffect(() => {
    startCamera(facingMode);
    // Intentionally only run once on mount; camera switching uses handleFlip
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleFlip = () => {
    if (state === 'recording') return;
    const next = facingMode === 'user' ? 'environment' : 'user';
    setFacingMode(next);
    startCamera(next);
  };

  const startRecording = () => {
    const stream = streamRef.current;
    if (!stream) return;

    const mimeType = getMimeType();
    if (!mimeType) {
      setError('Video recording is not supported in this browser.');
      return;
    }

    chunksRef.current = [];
    setElapsed(0);

    const recorder = new MediaRecorder(stream, { mimeType });
    mediaRecorderRef.current = recorder;

    recorder.ondataavailable = (event) => {
      if (event.data.size > 0) chunksRef.current.push(event.data);
    };

    recorder.onstop = () => {
      const contentType = mimeType.includes('mp4') ? 'video/mp4' : 'video/webm';
      const blob = new Blob(chunksRef.current, { type: contentType });
      blobRef.current = blob;
      const url = URL.createObjectURL(blob);
      setRecordedUrl(url);
      setState('recorded');
      stopAll();
    };

    recorder.start(250);
    setState('recording');

    timerRef.current = setInterval(() => {
      setElapsed((previousElapsed) => {
        const next = previousElapsed + 1;
        if (next * 1000 >= MAX_VIDEO_DURATION_MS) {
          recorder.stop();
        }
        return next;
      });
    }, 1000);
  };

  const stopRecording = () => {
    mediaRecorderRef.current?.stop();
  };

  const handleReRecord = () => {
    setRecordedUrl(null);
    blobRef.current = null;
    startCamera(facingMode);
  };

  const handleConfirm = () => {
    const blob = blobRef.current;
    if (!blob) return;
    const fileExtension = blob.type.includes('mp4') ? 'mp4' : 'webm';
    const file = new File([blob], `video-${Date.now()}.${fileExtension}`, {
      type: blob.type,
    });
    onRecordingComplete(file);
  };

  const formatTime = (secs: number) => {
    const seconds = secs % 60;
    return `0:${String(seconds).padStart(2, '0')}`;
  };

  const maxSecs = MAX_VIDEO_DURATION_MS / 1000;

  if (error) {
    return (
      <styled.Container>
        <styled.ErrorText>{error}</styled.ErrorText>
        <styled.Row>
          <styled.CancelBtn type="button" onClick={onCancel}>
            Cancel
          </styled.CancelBtn>
        </styled.Row>
      </styled.Container>
    );
  }

  return (
    <styled.Container>
      {state === 'recorded' && recordedUrl ? (
        <>
          <styled.PlaybackVideo
            ref={playbackRef}
            src={recordedUrl}
            controls
            playsInline
          />
          <styled.Row>
            <styled.SecondaryBtn type="button" onClick={handleReRecord}>
              Re-record
            </styled.SecondaryBtn>
            <styled.PrimaryBtn type="button" onClick={handleConfirm}>
              Use video
            </styled.PrimaryBtn>
          </styled.Row>
        </>
      ) : (
        <>
          <styled.VideoWrap>
            <styled.Video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              $mirrored={facingMode === 'user'}
            />
            {state === 'recording' && (
              <styled.Overlay>
                <styled.RecBadge>REC</styled.RecBadge>
                <styled.TimerOverlay>
                  {formatTime(elapsed)} / {formatTime(maxSecs)}
                </styled.TimerOverlay>
                <styled.ProgressBarBg>
                  <styled.ProgressBarFill
                    style={{ width: `${(elapsed / maxSecs) * 100}%` }}
                  />
                </styled.ProgressBarBg>
              </styled.Overlay>
            )}
            {state === 'idle' && (
              <styled.FlipBtn
                type="button"
                onClick={handleFlip}
                title="Flip camera"
              >
                🔄
              </styled.FlipBtn>
            )}
          </styled.VideoWrap>
          <styled.Row>
            <styled.CancelBtn type="button" onClick={onCancel}>
              Cancel
            </styled.CancelBtn>
            {state === 'idle' ? (
              <styled.PrimaryBtn type="button" onClick={startRecording}>
                Start recording
              </styled.PrimaryBtn>
            ) : (
              <styled.StopBtn type="button" onClick={stopRecording}>
                Stop
              </styled.StopBtn>
            )}
          </styled.Row>
        </>
      )}
    </styled.Container>
  );
};
